import { FC, MouseEvent, useCallback } from 'react';
import clsx from 'clsx';
import { identity, noop } from 'lodash-es';
import { Icon, IconSize } from '@namespace/icons2';
import { copyTextToClipboard } from '../../utils/copyTextToClipboard';
import { AText, ATextProps } from '../AText';
import { Box } from '../Box';

import styles from './index.module.css';

type Notification = {
  notificationType: string;
  message: string;
  timeout: number;
};

type GapOption = 's' | 'xxs' | 'xs' | 'm' | 'ms' | 'l' | 'xl' | 'xxl' | 'xxxl';

interface TextCopyProps extends Omit<ATextProps, 'color'> {
  textDisplay?: string | number;
  textCopy?: string | number;
  textNotify?: string | number;
  customNotification?: (notification: Notification) => void;
  t?: (key: string, values?: (string | number)[]) => string;
  color?: string;
  iconColor?: string;
  iconSize?: string;
  iconLink?: string;
  iconLeft?: boolean;
  gap?: GapOption;
  dataRoles?: {
    dataRoleText?: string;
    dataRoleIcon?: string;
  };
  classNames?: {
    copyTextWrapper?: string;
  };
  onCopyClick?: () => void;
}

export const TextCopy: FC<TextCopyProps> = ({
  textDisplay = '',
  textCopy = '',
  textNotify = '',
  customNotification = noop,
  t = identity,
  color,
  iconColor,
  iconSize,
  iconLink,
  iconLeft = false,
  gap = 's',
  dataRoles = {},
  classNames = {},
  onCopyClick,
  ...textProps
}) => {
  const notifyMessage = t('uikit.textCopy.notification', [textNotify]);
  const { dataRoleText, dataRoleIcon } = dataRoles;
  const { copyTextWrapper = '' } = classNames;

  const onClick = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      await copyTextToClipboard(String(textCopy));
      customNotification({
        notificationType: 'info',
        message: notifyMessage,
        timeout: 3000
      });

      if (onCopyClick) {
        onCopyClick();
      }
    },
    [notifyMessage, customNotification, textCopy, onCopyClick]
  );

  return (
    <Box
      gap={gap}
      onClick={onClick}
      align="center"
      className={clsx(styles.root, copyTextWrapper)}
    >
      {iconLeft && (
        <Icon
          size={(iconSize as IconSize) || 's'}
          name={iconLink || 'icons/general/action/copy'}
          color={iconColor || color}
          data-role={dataRoleIcon}
        />
      )}
      {textDisplay && (
        <AText {...textProps} color={color} data-role={dataRoleText}>
          {textDisplay}
        </AText>
      )}
      {!iconLeft && (
        <Icon
          size={(iconSize as IconSize) || 's'}
          name={iconLink || 'icons/general/action/copy'}
          color={iconColor || color}
          data-role={dataRoleIcon}
        />
      )}
    </Box>
  );
};
